import { ILot } from 'utils/interfaces'
import { LOT_TYPE } from 'utils/lotInterfaces'
import { canonicalizeLotModel, lotModelEquipmentProfile, REFILL_DRAIN_SKUS } from 'utils/stationLotModel'
import {
  SERVICE_CALIB_ICON,
  ServiceCalibIconId,
} from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibMapIcons'

export type StationFormType = 'charging' | 'refilling' | 'refilling_drain' | 'washing' | 'washing_omni'

export const STATION_FORM_TYPE_OPTIONS: { value: StationFormType; text: string }[] = [
  { value: 'charging', text: 'Charging' },
  { value: 'refilling', text: 'Refilling' },
  { value: 'refilling_drain', text: 'Refilling with drainage' },
  { value: 'washing', text: 'Washing (Sweeper / Combo)' },
  { value: 'washing_omni', text: 'Washing (Omni)' },
]

/**
 * Lot SKU when creating a station of this form type.
 * Prefer current firmware SKUs (`401-0`, `401-1`, `402-0`, `403-0`). Legacy `*-01`
 * stay valid on existing lots. `402-1` is the only create path for drainage.
 * `401-1` is the Omni washing dock, not an alias of `401-0`.
 */
export const STATION_TYPE_TO_LOT_MODEL: Record<StationFormType, string> = {
  charging: '403-0',
  refilling: '402-0',
  refilling_drain: '402-1',
  washing: '401-0',
  washing_omni: '401-1',
}

/** `<six digits>-<six digits>` */
export const STATION_ID_PATTERN = /^\d{6}-\d{6}$/

/** Example shown as field hint (matches real station SN style). */
export const STATION_ID_FORMAT_HINT = '123456-123456'

/** Latin letters/digits, spaces, hyphen, underscore — no Cyrillic (same rule as robot name). */
export const STATION_NAME_PATTERN = /^[a-zA-Z0-9\s\-_]+$/

export function isValidStationId(value: string): boolean {
  return STATION_ID_PATTERN.test(normalizeStationId(value))
}

/** Normalize spaces and unicode dashes so pasted IDs still match `\d{6}-\d{6}`. */
export function normalizeStationId(value: string): string {
  return (value || '')
    .trim()
    .replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, '-')
    .replace(/\s+/g, '')
}

export function isValidStationName(value: string): boolean {
  const name = (value || '').trim()
  return STATION_NAME_PATTERN.test(name) && /[a-zA-Z0-9]/.test(name)
}

/** Map station lots to form type. SKU first (402-0 vs 402-1); Lot.type as fallback. Multiple lots → Washing. */
export function resolveStationTypeFromLots(lots: ILot[]): StationFormType {
  if (lots.length !== 1) {
    return 'washing'
  }

  const sku = canonicalizeLotModel(lots[0].lot_model)
  if (sku === '401-1') {
    return 'washing_omni'
  }
  if (REFILL_DRAIN_SKUS.has(sku)) {
    return 'refilling_drain'
  }

  const profile = lotModelEquipmentProfile(lots[0].lot_model)
  if (profile === 'charging') {
    return 'charging'
  }
  if (profile === 'refill') {
    return 'refilling'
  }
  if (profile === 'washing') {
    return 'washing'
  }

  switch (lots[0].type) {
    case LOT_TYPE.CHARGING:
      return 'charging'
    case LOT_TYPE.REFILLING:
      return 'refilling'
    case LOT_TYPE.WASHING:
    default:
      return 'washing'
  }
}

export function defaultLotNameForStation(stationName: string): string {
  return stationName.trim() || 'Station'
}

export function stationFormTypeToMapIconId(type: StationFormType): ServiceCalibIconId {
  switch (type) {
    case 'charging':
      return SERVICE_CALIB_ICON.CHARGING
    case 'refilling':
    case 'refilling_drain':
      return SERVICE_CALIB_ICON.REFILLING
    case 'washing':
    case 'washing_omni':
    default:
      return SERVICE_CALIB_ICON.WASHING
  }
}
